import { classifyCounterpartyConcept } from './counterparty_comment_classification.js';
import {
  deriveCounterpartySeverity,
  normalizeCounterpartyEffects,
} from './counterparty_comment_effects.js';
// The bracket pair, not the well-formed token, is the rule lp-005 arrived at
// for prose the model authors: document_patch_schema's `atom-free-text` refuses
// on `ATOM_BRACKET` alone (document_patch_schema.js:210). Matching only a
// complete ⟦f7:Base Rent⟧ would admit the worse cases — `⟦f7:Base Rent` and
// `⟦Base Rent⟧` are fabricated references too, and a half-formed one is not
// caught by any later reader, so it reaches the document as literal garbage.
// ⟦⟧ are reserved to the projection grammar and never occur in lease prose.
const ATOM_BRACKET = /[⟦⟧]/;

function reject(rejected, logger, index, entry, reason) {
  rejected.push({ index, entry, reason });
  logger.warn(`Rejected counterparty comment at index ${index}: ${reason}`);
}

function validDiffIndex(value) {
  return Number.isInteger(value) && value >= 0;
}

function revisionRefusal(entry) {
  if (
    typeof entry.revisedText === 'string' &&
    ATOM_BRACKET.test(entry.revisedText)
  ) {
    return 'revisedText must not contain an atom token';
  }

  if (
    entry.verdict === 'revise' &&
    (typeof entry.revisedText !== 'string' || entry.revisedText.trim() === '')
  ) {
    return 'revisedText is required when verdict is revise';
  }

  return null;
}

/**
 * Validate each review independently so malformed model output cannot discard
 * usable neighbours. A diff-index addresses its slot in diffgram.items; only
 * an existing unresolved slot can receive commentary. A concept outside the
 * vocabulary leaves the entry attached to that slot with `unclassified` set,
 * which is the queue the card reads — it is never a reason to refuse.
 */
export function validateCounterpartyCommentResponse(
  entries,
  changes,
  vocabulary,
  logger = console,
) {
  const reviews = [];
  const rejected = [];
  const seenDiffIndexes = new Set();
  const reviewChanges = Array.isArray(changes) ? changes : [];

  if (!Array.isArray(entries)) {
    reject(rejected, logger, 0, entries, 'response must be an array');
    return { reviews, rejected };
  }

  function validateEntry(entry, index) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      reject(rejected, logger, index, entry, 'entry must be an object');
      return;
    }

    if (!validDiffIndex(entry.diffIndex)) {
      reject(
        rejected,
        logger,
        index,
        entry,
        'diffIndex must be a non-negative integer',
      );
      return;
    }

    if (seenDiffIndexes.has(entry.diffIndex)) {
      reject(
        rejected,
        logger,
        index,
        entry,
        `duplicate diffIndex: ${entry.diffIndex}`,
      );
      return;
    }
    seenDiffIndexes.add(entry.diffIndex);

    const change = reviewChanges[entry.diffIndex];
    if (!change) {
      reject(
        rejected,
        logger,
        index,
        entry,
        `diffIndex does not name an existing change: ${entry.diffIndex}`,
      );
      return;
    }
    if (change._status !== 'unresolved') {
      reject(
        rejected,
        logger,
        index,
        entry,
        `diffIndex does not name an unresolved change: ${entry.diffIndex}`,
      );
      return;
    }

    const revisionReason = revisionRefusal(entry);
    if (revisionReason) {
      reject(rejected, logger, index, entry, revisionReason);
      return;
    }

    // Named rather than splatted so this file states its own output contract:
    // the card built in P2-05 reads `unclassified` to pick its state, and a
    // spread would leave that field discoverable only in another module.
    const {
      concept,
      classificationAttempted,
      unclassified,
    } = classifyCounterpartyConcept(entry.concept, vocabulary);
    const effects = normalizeCounterpartyEffects(entry.effects);

    reviews.push({
      ...entry,
      concept,
      classificationAttempted,
      unclassified,
      effects,
      severity: deriveCounterpartySeverity(effects),
    });
  }

  entries.forEach((entry, index) => {
    // The refusals above enumerate the malformed shapes we know of; this catch
    // covers the ones we do not. In lp-006 a single unanticipated RangeError
    // escaped the per-item work and "took down the whole batch rather than the
    // single patch" (document_patch_compiler.js:911). Refusing only the entry
    // that threw — reported as unexpected, never disguised as one of the
    // ordinary refusals — is what makes the isolation total rather than a list
    // of the failures we happened to think of first.
    try {
      validateEntry(entry, index);
    } catch (error) {
      reject(
        rejected,
        logger,
        index,
        entry,
        `unexpected error while validating entry: ${(error && error.message) ||
          error}`,
      );
    }
  });

  return { reviews, rejected };
}
